import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import graphService from "../../services/graphService";
import groupService from "../../services/groupService";
import topicService from "../../services/topicService";
import { toast } from "react-toastify";

const initialState = {
  graphData: null,
  loading: false,
  error: null,
  tags: [],
  showPopup: false,
  pinnedNodes: new Set(),
  expandedNodes: new Set(),
  resetableNodes: new Set(),
  edgeCount: 0,
  initialNodesCount: 0,
  expandedNodesCount: 0,
  totalNodesCount: 0,
  isCollapseAll: false,
  allExpandedNodes: new Set(),
  filters: {
    includeClasses: new Set(),
    includeTopics: new Set(),
    includeKeywords: new Set(),
    includeManualAndExpand: new Set(),
    includeIds: new Set(),
  },
  selectedLeafClasses: [],
  filterNode: null,
  isCopyLink: false,
  version: null,
  resetTriggered: false,
  fetchedNodesData: {
    nodes: [],
    edges: [],
  },
  expandedNodesMap: {},
  expandedPinnedNodes: new Set(),
  selectedSearchId: null,
};

let currentRequestId = 0;

export const fetchGraphDataThunk = createAsyncThunk(
  "graph/fetchGraphData",
  async (
    { userId, selectedClasses },
    { getState, requestId, rejectWithValue }
  ) => {
    try {
      const state = getState();
      const includeKeywords = state.graph.filters.includeKeywords;

      // Check if selectedClasses is an array and has length
      if (Array.isArray(selectedClasses) && selectedClasses.length > 0) {
        currentRequestId = requestId;

        const result = await graphService.fetchGraphData(
          userId,
          selectedClasses,
          includeKeywords
        );

        if (currentRequestId !== requestId) {
          return null;
        }

        return result;
      } else {
        return rejectWithValue("selectedClasses is empty or invalid");
      }
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const handleSearchSelectThunk = createAsyncThunk(
  "graph/handleSearchSelect",
  async ({ searchId, userId }, { rejectWithValue }) => {
    try {
      return await graphService.handleSearchSelect(searchId, userId);
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const handleTagChangeThunk = createAsyncThunk(
  "graph/handleTagChange",
  async ({ userId, newTags }, { getState, rejectWithValue }) => {
    const state = getState();

    // Extract the current filter values from Redux state
    const includeClasses = Array.from(state.graph.filters.includeClasses || []);

    const payload = {
      includeClasses: includeClasses.length > 0 ? includeClasses : [],
      includeTopics: [],
      includeKeywords: newTags,
      includeManualAndExpand: [],
    };

    try {
      if (newTags?.length > 0) {
        return await graphService.fetchGraphDataByTags(userId, payload);
      } else {
        return null;
      }
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const handleAttachmentGroupThunk = createAsyncThunk(
  "graph/handleAttachmentGroupThunk",
  async ({ userId }, { getState }) => {
    const state = getState();

    // Extract the current filter values from Redux state
    const includeIds = Array.from(state.graph.filters.includeIds || []);

    // Fetch graph data using the includeKeywords
    const searchResponse = await groupService.searchGraphData(
      userId,
      includeIds,
      []
    );

    return searchResponse;
  }
);

export const handleCreateModelThunk = createAsyncThunk(
  "graph/handleCreateModel",
  async (
    { userId = 1, nodes, startNode, nameRequired, fetchNode },
    { rejectWithValue, dispatch }
  ) => {
    try {
      const createResponse = await groupService.createGroupNodes(
        nodes,
        startNode,
        nameRequired
      );

      // Check if the response is successful
      if (createResponse?.status === "success" && createResponse.createdNodes) {
        // Extract the created nodes and match with fetchNode
        const createdNodeKeys = Object.keys(createResponse.createdNodes);

        const includeIds =
          nameRequired && nameRequired === "true"
            ? createdNodeKeys
                .filter(
                  (nodeKey) => nodeKey === fetchNode // Match fetchNode with created nodes
                )
                .map((nodeKey) => createResponse.createdNodes[nodeKey]?.id)
            : [createResponse.createdNodes.id];

        // Dispatch action to clear includeIds
        dispatch(setIncludeIds(includeIds));

        // Dispatch action to update includeKeywords
        dispatch(setIncludeKeywords([]));

        // Fetch graph data using the includeKeywords
        const searchResponse = await groupService.searchGraphData(
          userId,
          includeIds,
          []
        );

        return searchResponse;
      } else {
        toast.error("Error while creating the group");
      }
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const handleTopicSelectionThunk = createAsyncThunk(
  "graph/handleTopicSelection",
  async ({ userId, selectedTopics }, { rejectWithValue }) => {
    try {
      if (selectedTopics.length > 0) {
        return await topicService.searchTopics(userId, selectedTopics);
      } else {
        return null;
      }
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

const validateAndProcessEdges = (edges, availableNodes, fetchedNodes = []) => {
  const allNodes = new Set([
    ...availableNodes.map((node) => String(node.id)),
    ...fetchedNodes.map((node) => String(node.id)),
  ]);

  return edges?.filter((edge) => {
    const sourceExists = allNodes.has(String(edge.source));
    const targetExists = allNodes.has(String(edge.target));
    return sourceExists && targetExists;
  });
};

export const fetchAllChildGraphData = createAsyncThunk(
  "graph/fetchAllChildGraphData",
  async ({ userId, nodeId }, { rejectWithValue }) => {
    try {
      const payload = { nodeid: nodeId }; // Set up payload with nodeid key
      const response = await graphService.fetchAllChildData(userId, nodeId);
      return response;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const resetGraph = createAsyncThunk(
  "graph/resetGraph",
  async ({ userId }, { getState, dispatch }) => {
    const state = getState();
    const pinnedNodes = Array.from(state.graph.pinnedNodes).map((id) =>
      String(id)
    );
    const expandedNodes = Array.from(state.graph.expandedNodes).map((id) =>
      String(id)
    );
    const allExpandedNodes = Array.from(state.graph.allExpandedNodes).map(
      (id) => String(id)
    );
    // const fetchedNodesData = state.graph.fetchedNodesData; // Get fetched data from state

    // Case 1: No pinned or expanded nodes
    if (
      pinnedNodes.length === 0 &&
      expandedNodes.length === 0 &&
      allExpandedNodes.length === 0
    ) {
      dispatch(clearGraphData());
      dispatch(setResetTriggered(false));
      return null;
    }

    dispatch(setResetTriggered(true));

    try {
      let finalResponse = { nodes: [], edges: [] };

      // Get pinned nodes if any
      if (pinnedNodes.length > 0) {
        const pinnedResponse = await graphService.graphSearch(userId, {
          includeClasses: [],
          includeTopics: [],
          includeKeywords: [],
          includeManualAndExpand: [],
          includeIds: pinnedNodes.map((id) => parseInt(id, 10)),
        });

        if (pinnedResponse) {
          // Filter edges to remove self-loops (edges where source and target are the same)
          const filteredEdges = pinnedResponse.edges?.filter(
            (edge) => edge.source !== edge.target
          );
          pinnedResponse.edges = filteredEdges;
          finalResponse.nodes = [...(pinnedResponse.nodes || [])];
          finalResponse.edges = [...(pinnedResponse.edges || [])];
        }
      }

      // Add expanded nodes data
      // if (expandedNodes.length > 0 || allExpandedNodes.length > 0) {
      //   // Add nodes from fetched data
      //   if (fetchedNodesData.nodes) {
      //     fetchedNodesData.nodes.forEach((node) => {
      //       if (!finalResponse.nodes.some((n) => n.id === node.id)) {
      //         finalResponse.nodes.push(node);
      //       }
      //     });
      //   }

      //   // Add edges from fetched data
      //   if (fetchedNodesData.edges) {
      //     fetchedNodesData.edges.forEach((edge) => {
      //       if (!finalResponse.edges.some((e) => e.id === edge.id)) {
      //         finalResponse.edges.push(edge);
      //       }
      //     });
      //   }
      // }

      return finalResponse;
    } catch (error) {
      console.error("Error in resetGraph:", error);
      throw error;
    }
  }
);

// Add removeGraphNodeId Thunk
export const removeGraphNodeId = createAsyncThunk(
  "graph/removeGraphNodeId",
  async ({ nodeId }, { getState, dispatch }) => {
    const state = getState();
    const includeIds = new Set(state.graph.filters.includeIds);
    if (includeIds.has(nodeId)) {
      includeIds.delete(nodeId);
      dispatch(setIncludeIds(Array.from(includeIds))); // Update state with new includeIds
    }
  }
);

// Modify createGraphNode to update includeIds
export const createGraphNode = createAsyncThunk(
  "graph/createGraphNode",
  async ({ userId, nodeId }, { getState, dispatch }) => {
    try {
      const state = getState();

      // Extract the current filter values from Redux state
      const includeClasses = Array.from(
        state.graph.filters.includeClasses || []
      );
      const includeTopics = Array.from(state.graph.filters.includeTopics || []);
      const includeKeywords = Array.from(
        state.graph.filters.includeKeywords || []
      );
      const allExpandedNodes = Array.from(state.graph.allExpandedNodes || []);
      const includeIds = new Set(state.graph.filters.includeIds || []);

      // Add the provided nodeId to includeIds if it exists
      if (nodeId) includeIds.add(nodeId);

      // Prepare the payload with all filter values
      const payload = {
        includeClasses: includeClasses.length > 0 ? includeClasses : [],
        includeTopics: includeTopics.length > 0 ? includeTopics : [],
        includeKeywords: [],
        includeManualAndExpand:
          allExpandedNodes.length > 0 ? allExpandedNodes : [], // Assuming this remains empty as per the original code
        includeIds: Array.from(includeIds),
      };

      // Make the API call with the updated payload
      const response = await graphService.graphSearch(userId, payload);

      // Dispatch actions to update the Redux state
      dispatch(setIncludeIds(Array.from(includeIds))); // Update includeIds in state
      return response;
    } catch (error) {
      console.error("Error in createGraphNode:", error);
      throw error;
    }
  }
);

export const removeGraphNode = createAsyncThunk(
  "graph/removeNode",
  async (nodeId, { getState, dispatch }) => {
    const state = getState();

    if (!state.graph.graphData?.nodes) {
      throw new Error("Graph data not initialized.");
    }

    // Find the node to be removed
    const nodeToRemove = [
      ...state.graph.graphData.nodes,
      ...state.graph.fetchedNodesData.nodes,
    ].find((node) => node.id === nodeId);

    if (!nodeToRemove) {
      throw new Error(`Node with id ${nodeId} not found.`);
    }

    // Filter nodes and edges
    const filteredNodes = state.graph.graphData.nodes.filter(
      (node) => node.id !== nodeId
    );
    const filteredEdges = state.graph.graphData.edges.filter(
      (edge) => edge.source !== nodeId && edge.target !== nodeId
    );
    const filteredFetchedNodes = state.graph.fetchedNodesData.nodes.filter(
      (node) => node.id !== nodeId
    );
    const filteredFetchedEdges = state.graph.fetchedNodesData.edges.filter(
      (edge) => edge.source !== nodeId && edge.target !== nodeId
    );

    // Dispatch actions to update expanded nodes
    dispatch(updateExpandedNodes({ nodeId, expanded: false }));
    dispatch(removeExpandedNode(nodeId));

    // Update fetched nodes data
    dispatch(
      setFetchedNodesData({
        nodes: filteredFetchedNodes || [],
        edges: filteredFetchedEdges || [],
      })
    );

    // Filter node types based on the remaining nodes
    const remainingNodeTypes = [
      ...new Set(filteredNodes.map((node) => node.type)),
    ];

    return {
      nodes: filteredNodes,
      edges: filteredEdges,
      node_types: remainingNodeTypes,
    };
  }
);

// Update the pin/unpin actions to preserve expanded state
export const pinNode = createAsyncThunk(
  "graph/pinNode",
  async (nodeId, { getState }) => {
    const state = getState();
    const isExpanded =
      state.graph.expandedNodes.has(String(nodeId)) ||
      state.graph.allExpandedNodes.has(String(nodeId));
    return { nodeId: String(nodeId), isExpanded };
  }
);

export const unpinNode = createAsyncThunk(
  "graph/unpinNode",
  async (nodeId, { getState }) => {
    const state = getState();
    const isExpanded =
      state.graph.expandedNodes.has(String(nodeId)) ||
      state.graph.allExpandedNodes.has(String(nodeId));
    return { nodeId: String(nodeId), isExpanded };
  }
);

export const unpinAllNodes = createAsyncThunk(
  "graph/unpinAllNodes",
  async () => {
    return [];
  }
);

export const resetUnpinnedNodes = createAsyncThunk(
  "graph/resetUnpinnedNodes",
  async (_, { getState }) => {
    const state = getState().graph;
    return {
      pinnedNodes: state.pinnedNodes,
      // expandedNodes: new Set(),
      resetableNodes: new Set(),
    };
  }
);

export const updateEdgeCount = createAsyncThunk(
  "graph/updateEdgeCount",
  async (count) => {
    return count;
  }
);

export const fetchFilteredGraphData = createAsyncThunk(
  "graph/fetchFilteredData",
  async ({ userId }, { getState, rejectWithValue }) => {
    try {
      const state = getState().graph;

      // Convert Sets to arrays for the API call
      const payload = {
        includeClasses: Array.from(state.filters.includeClasses),
        includeTopics: Array.from(state.filters.includeTopics),
        includeKeywords: Array.from(state.filters.includeKeywords),
        includeManualAndExpand: Array.from(
          state.filters.includeManualAndExpand
        ),
        includeIds: Array.from(state.filters.includeIds),
      };

      const response = await graphService.graphSearch(userId, payload);
      return response;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Add new action to set filter node and copy link status
export const setFilterNodeAndCopyLink = createAsyncThunk(
  "graph/setFilterNodeAndCopyLink",
  async ({ filterNode, isCopyLink, version }) => {
    return { filterNode, isCopyLink, version };
  }
);

export const fetchPinnedNodes = createAsyncThunk(
  "graph/fetchPinnedNodes",
  async ({ userId }, { getState, rejectWithValue }) => {
    try {
      const state = getState().graph;

      if (state.pinnedNodes.length === 0) {
        return { nodes: [], edges: [] }; // Return empty if no pinned nodes
      }

      // Perform the graph search
      const pinnedResponse = await graphService.graphSearch(userId, {
        includeClasses: [],
        includeTopics: [],
        includeKeywords: [],
        includeManualAndExpand: [],
        includeIds: Array.from(state.pinnedNodes).map((id) => parseInt(id, 10)),
      });

      if (pinnedResponse) {
        return pinnedResponse;
      }

      return null;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

const graphSlice = createSlice({
  name: "graph",
  initialState,
  reducers: {
    clearGraphData: (state) => {
      state.graphData = null;
      state.edgeCount = 0;
      state.initialNodesCount = 0;
      state.expandedNodesCount = 0;
      state.totalNodesCount = 0;
      state.expandedNodes = new Set();
      state.isCollapseAll = false;
      state.allExpandedNodes = new Set();
      state.resetTriggered = false;
      state.expandedNodesMap = {};
      state.pinnedNodes = new Set();
      state.filterNode = null;
      state.expandedPinnedNodes = new Set();
      state.selectedSearchId = null;
      state.isCopyLink = false;
      state.selectedLeafClasses = [];
    },
    setFetchedNodesData: (state, action) => {
      state.fetchedNodesData = action.payload;
    },
    setResetTriggered: (state, action) => {
      state.resetTriggered = action.payload;
    },
    addResetableNode: (state, action) => {
      state.resetableNodes.add(action.payload);
    },
    setIncludeClasses: (state, action) => {
      state.filters.includeClasses = new Set(action.payload);
    },
    setIncludeTopics: (state, action) => {
      state.filters.includeTopics = new Set(action.payload);
    },
    setIncludeKeywords: (state, action) => {
      state.filters.includeKeywords = new Set(action.payload);
    },
    setIncludeManualAndExpand: (state, action) => {
      state.filters.includeManualAndExpand = new Set(action.payload);
    },
    setIncludeIds: (state, action) => {
      state.filters.includeIds = new Set(action.payload);
    },
    resetFilters: (state) => {
      state.filters = initialState.filters;
      state.selectedLeafClasses = [];
    },
    setSelectedLeafClasses: (state, action) => {
      state.selectedLeafClasses = action.payload;
      if (!action.payload) {
        state.pinnedNodes = new Set();
      }
    },
    setInitialNodesCount: (state, action) => {
      state.initialNodesCount = action.payload;
      state.totalNodesCount = action.payload;
    },
    updateExpandedNodes: (state, action) => {
      const { nodeId, expanded } = action.payload;

      if (expanded) {
        state.expandedNodes.add(nodeId);
      } else {
        state.expandedNodes.delete(parseInt(nodeId));
      }

      state.expandedNodesCount = state.expandedNodes.size;
      state.totalNodesCount =
        state.initialNodesCount + state.expandedNodes.size;
    },
    collapseExpandedNodes: (state) => {
      state.expandedNodesCount = 0;
      state.totalNodesCount = state.initialNodesCount;
      state.expandedNodesMap = {};
      // Filter allExpandedNodes by checking if the node exists in pinnedNodes (which is a Set)
      const filteredPinnedNodes = new Set(
        [...state.allExpandedNodes, ...state.expandedNodes].filter(
          (node) => state.pinnedNodes.has(String(node)) // Check if the node is in the pinnedNodes set
        )
      );
      // Update the allExpandedNodes with the filtered pinned nodes
      state.expandedPinnedNodes = filteredPinnedNodes;
      state.allExpandedNodes = new Set();
      state.expandedNodes = new Set();
    },
    toggleCollapseAll: (state) => {
      state.isCollapseAll = !state.isCollapseAll;
    },
    addExpandedNode: (state, action) => {
      state.allExpandedNodes.add(action.payload);
    },
    removeExpandedNode: (state, action) => {
      state.allExpandedNodes.delete(action.payload);
    },
    removeAllExpandedNodes: (state, action) => {
      state.allExpandedNodes = new Set();
      state.expandedNodes = new Set();
      state.expandedNodesCount = 0;
      state.expandedNodesMap = {};
    },
    addToFilter: (state, action) => {
      const { filterType, values } = action.payload;
      if (filterType in state.filters) {
        values.forEach((value) => state.filters[filterType].add(value));
      }
    },

    removeFromFilter: (state, action) => {
      const { filterType, values } = action.payload;
      if (filterType in state.filters) {
        values.forEach((value) => state.filters[filterType].delete(value));
      }
    },

    clearSpecificFilter: (state, action) => {
      const filterType = action.payload;
      if (filterType in state.filters) {
        state.filters[filterType] = new Set();
      }
    },

    clearAllFilters: (state) => {
      Object.keys(state.filters).forEach((key) => {
        state.filters[key] = new Set();
      });
    },

    updateMultipleFilters: (state, action) => {
      const updates = action.payload;
      Object.keys(updates).forEach((filterType) => {
        if (filterType in state.filters) {
          state.filters[filterType] = new Set(updates[filterType]);
        }
      });
    },
    setFilterNode: (state, action) => {
      state.filterNode = action.payload;
    },
    setIsCopyLink: (state, action) => {
      state.isCopyLink = action.payload;
    },
    setVersion: (state, action) => {
      state.version = action.payload;
    },
    clearFilterAndCopyLink: (state) => {
      // state.filterNode = null;
      state.isCopyLink = false;
      // state.version = null;
    },
    trackExpandedNodes: (state, action) => {
      const { nodeId, updatedExpandedNodes } = action.payload;
      state.expandedNodesMap[nodeId] = updatedExpandedNodes;
    },
    updateTrackExpandedNodes: (state, action) => {
      const { nodeId, expanded } = action.payload;
      if (expanded) {
        state.expandedNodesMap[nodeId] = state.expandedNodesMap[nodeId] || [];
      } else {
        delete state.expandedNodesMap[nodeId];
      }
    },
    removeTrackExpandedNodes: (state, action) => {
      const nodeId = action.payload;
      delete state.expandedNodesMap[nodeId];
    },
    setSearchId: (state, action) => {
      state.selectedSearchId = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchGraphDataThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchGraphDataThunk.fulfilled, (state, action) => {
        state.loading = false;

        if (
          state.filters.includeClasses &&
          state.filters.includeClasses.size > 0
        ) {
          const {
            nodes = [],
            edges = [],
            node_types = {},
          } = action.payload || {};

          // Filter edges to remove self-loops
          const filteredEdges = edges.filter(
            (edge) => edge.source !== edge.target
          );

          state.graphData = { nodes, edges: filteredEdges, node_types };
        } else {
          // Handle cases where filters are empty or invalid
          state.graphData = { nodes: [], edges: [], node_types: {} };
        }
      })
      .addCase(fetchGraphDataThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(handleSearchSelectThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(handleSearchSelectThunk.fulfilled, (state, action) => {
        state.loading = false;

        const { nodes, edges, node_types } = action.payload;

        // Filter edges to remove self-loops (edges where source and target are the same)
        const filteredEdges = edges?.filter(
          (edge) => edge.source !== edge.target
        );
        state.graphData = { nodes, edges: filteredEdges, node_types };
      })
      .addCase(handleSearchSelectThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(handleTagChangeThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(handleTagChangeThunk.fulfilled, (state, action) => {
        state.loading = false;

        const { nodes, edges, node_types } = action.payload;

        // Filter edges to remove self-loops (edges where source and target are the same)
        const filteredEdges = edges?.filter(
          (edge) => edge.source !== edge.target
        );
        state.graphData = { nodes, edges: filteredEdges, node_types };
      })
      .addCase(handleTagChangeThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(handleAttachmentGroupThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(handleAttachmentGroupThunk.fulfilled, (state, action) => {
        state.loading = false;

        const { nodes, edges, node_types } = action.payload;

        // Filter edges to remove self-loops (edges where source and target are the same)
        const filteredEdges = edges?.filter(
          (edge) => edge.source !== edge.target
        );
        state.graphData = { nodes, edges: filteredEdges, node_types };
      })
      .addCase(handleAttachmentGroupThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(handleCreateModelThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(handleCreateModelThunk.fulfilled, (state, action) => {
        state.loading = false;

        const { nodes, edges, node_types } = action.payload;

        // Filter edges to remove self-loops (edges where source and target are the same)
        const filteredEdges = edges?.filter(
          (edge) => edge.source !== edge.target
        );
        state.graphData = { nodes, edges: filteredEdges, node_types };
        state.showPopup = false;
      })
      .addCase(handleCreateModelThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.showPopup = false;
      })
      .addCase(handleTopicSelectionThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(handleTopicSelectionThunk.fulfilled, (state, action) => {
        state.loading = false;

        if (
          Array.isArray(state.selectedLeafClasses) &&
          state.selectedLeafClasses.length > 0
        ) {
          const {
            nodes = [],
            edges = [],
            node_types = {},
          } = action.payload || {};

          // Filter edges to remove self-loops
          const filteredEdges = edges.filter(
            (edge) => edge.source !== edge.target
          );

          state.graphData = { nodes, edges: filteredEdges, node_types };
        } else {
          // Handle cases where selectedLeafClasses is empty or invalid
          state.graphData = { nodes: [], edges: [], node_types: {} };
        }
      })
      .addCase(handleTopicSelectionThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(pinNode.fulfilled, (state, action) => {
        const { nodeId, isExpanded } = action.payload;
        state.pinnedNodes.add(nodeId);
        // Preserve expanded state if node was expanded
        if (isExpanded) {
          state.expandedNodes.add(nodeId);
        }
      })
      .addCase(unpinNode.fulfilled, (state, action) => {
        const { nodeId, isExpanded } = action.payload;
        state.pinnedNodes.delete(nodeId);
        // Keep expanded state if node was expanded
        if (!isExpanded) {
          state.expandedNodes.delete(nodeId);
        }
      })
      .addCase(unpinAllNodes.fulfilled, (state) => {
        state.pinnedNodes = new Set();
      })
      .addCase(updateEdgeCount.fulfilled, (state, action) => {
        state.edgeCount = action.payload;
      })
      .addCase(resetUnpinnedNodes.fulfilled, (state, action) => {
        state.pinnedNodes = action.payload.pinnedNodes;
        state.expandedNodes = action.payload.expandedNodes;
        state.resetableNodes = action.payload.resetableNodes;
      })
      .addCase(resetGraph.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(resetGraph.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload) {
          // Keep the expanded nodes sets intact
          const currentExpandedNodes = new Set(state.expandedNodes);
          const currentAllExpandedNodes = new Set(state.allExpandedNodes);

          // Update the graph data
          const { nodes, edges, node_types } = action.payload;

          // Filter edges to remove self-loops (edges where source and target are the same)
          const filteredEdges = edges?.filter(
            (edge) => edge.source !== edge.target
          );
          state.graphData = { nodes, edges: filteredEdges, node_types };
          state.edgeCount = filteredEdges?.length || 0;
          state.initialNodesCount = action.payload.nodes?.length || 0;

          // Restore expanded nodes sets
          // state.expandedNodes = currentExpandedNodes;
          // state.allExpandedNodes = currentAllExpandedNodes;
          // state.expandedNodesCount = currentExpandedNodes.size;
          state.totalNodesCount = state.initialNodesCount;
          state.selectedLeafClasses = [];
        }
        state.resetableNodes = new Set();
      })
      .addCase(resetGraph.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      .addCase(createGraphNode.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createGraphNode.fulfilled, (state, action) => {
        state.loading = false;

        const { nodes, edges, node_types } = action.payload;

        // Filter edges to remove self-loops (edges where source and target are the same)
        const filteredEdges = edges?.filter(
          (edge) => edge.source !== edge.target
        );
        // Merge new nodes, edges, and node types with existing graph data
        const existingNodes = state.graphData?.nodes || [];
        const existingEdges = state.graphData?.edges || [];
        const existingNodeTypes = state.graphData?.node_types || [];

        state.graphData = {
          nodes: [...existingNodes, ...(nodes ?? [])],
          edges: [...existingEdges, ...(filteredEdges ?? [])],
          node_types: [
            ...new Set([...existingNodeTypes, ...(node_types ?? [])]),
          ],
        };
      })
      .addCase(createGraphNode.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(removeGraphNode.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(removeGraphNode.fulfilled, (state, action) => {
        state.loading = false;

        state.graphData = action.payload;
      })
      .addCase(removeGraphNode.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchFilteredGraphData.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchFilteredGraphData.fulfilled, (state, action) => {
        state.loading = false;

        if (
          state.filters.includeClasses.size > 0 ||
          state.filters.includeKeywords.size > 0 ||
          state.filters.includeIds.size > 0 ||
          state.selectedLeafClasses?.length > 0
        ) {
          const {
            nodes = [],
            edges = [],
            node_types = {},
          } = action.payload || {};

          // Filter edges to remove self-loops
          const filteredEdges = edges.filter(
            (edge) => edge.source !== edge.target
          );
          if (filteredEdges) {
            state.edgeCount = filteredEdges.length;
          }

          state.graphData = { nodes, edges: filteredEdges, node_types };
        } else {
          // Handle cases where selectedLeafClasses is empty or invalid
          state.graphData = { nodes: [], edges: [], node_types: {} };
        }
      })
      .addCase(fetchFilteredGraphData.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchAllChildGraphData.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAllChildGraphData.fulfilled, (state, action) => {
        state.loading = false;

        const { nodes, edges, node_types } = action.payload;

        // Filter edges to remove self-loops (edges where source and target are the same)
        const filteredEdges = edges?.filter(
          (edge) => edge.source !== edge.target
        );
        state.graphData = { nodes, edges: filteredEdges, node_types };
      })
      .addCase(fetchAllChildGraphData.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(setFilterNodeAndCopyLink.fulfilled, (state, action) => {
        state.filterNode = action.payload.filterNode;
        state.isCopyLink = action.payload.isCopyLink;
        state.version = action.payload.version;
      })
      .addCase(fetchPinnedNodes.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPinnedNodes.fulfilled, (state, action) => {
        state.loading = false;

        const { nodes, edges, node_types } = action.payload;

        // Filter edges to remove self-loops (edges where source and target are the same)
        const filteredEdges = edges?.filter(
          (edge) => edge.source !== edge.target
        );
        state.graphData = { nodes, edges: filteredEdges, node_types };
      })
      .addCase(fetchPinnedNodes.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const {
  clearGraphData,
  addResetableNode,
  setIncludeClasses,
  setIncludeTopics,
  setIncludeKeywords,
  setIncludeManualAndExpand,
  setIncludeIds,
  resetFilters,
  setSelectedLeafClasses,
  setInitialNodesCount,
  updateExpandedNodes,
  collapseExpandedNodes,
  toggleCollapseAll,
  addExpandedNode,
  removeExpandedNode,
  addToFilter,
  removeFromFilter,
  clearSpecificFilter,
  clearAllFilters,
  updateMultipleFilters,
  setFilterNode,
  setIsCopyLink,
  setVersion,
  clearFilterAndCopyLink, // Add this export
  setResetTriggered, // Export this action
  setFetchedNodesData,
  trackExpandedNodes,
  updateTrackExpandedNodes,
  removeTrackExpandedNodes,
  removeAllExpandedNodes,
  setSearchId,
} = graphSlice.actions;
export default graphSlice.reducer;
